import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from "react";
import { useUser } from "../UserContext/UserProvider";
import { backendUrl } from "../../api/constants";
import { createCategoryIcons } from "../../util/mapStyles";
import { injectCategoryStyles } from "../../util/categoryStyles";
import SuspenseLoader from "../../components/SuspenseLoader/SuspenseLoader";
import axios from "axios";
import axiosInstance from "../../api/axiosInstance";

const newYorkCoords = {
  lat: 40.717426429835896,
  lng: -74.00518256903389,
};

const preDefinedLocations = [
  {
    id: "new-york",
    name: "New York",
    lat: newYorkCoords.lat,
    lng: newYorkCoords.lng,
    timezone: "America/New_York",
  },
];

const DataContext = createContext({});
export const useData = () => useContext(DataContext) || {};

export const DataProvider = ({ children }) => {
  const [globalConfig, setGlobalConfig] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedTimezone, setSelectedTimezone] = useState(null);
  const [loading, setLoading] = useState(false);
  const { setUser } = useUser();
  const [userLocationLoading, setUserLocationLoading] = useState(false);

  const fetchConfig = async () => {
    try {
      const { data: response } = await axios.get(`${backendUrl}/config`);
      console.log(response);
      if (!response.data?.categories) {
        // refetch after 2 seconds
        setTimeout(fetchConfig, 2000);
        return;
      }
      const normalized = {
        ...response.data,
        knownServices: response.data.knownServices || {},
      };
      setGlobalConfig(normalized);
      createCategoryIcons(normalized.categories);
      // Inject CSS custom properties for performance
      injectCategoryStyles(normalized.categories);
    } catch (error) {
      console.log(error);
      setTimeout(fetchConfig, 2000);
    }
  };

  // Memoize handler functions to prevent unnecessary re-renders
  const toggleFollowUser = useCallback((userId) => {
    setUser((prev) => ({
      ...prev,
      following: prev.following.includes(userId)
        ? prev.following.filter((id) => id !== userId)
        : [...prev.following, userId],
    }));
  }, [setUser]);

  const getEventTimezone = (ev) => {
    // If the event has a venue, use the venue's timezone
    if (ev.venue) {
      const location = getLocationById(ev.venue);
      return location?.timezone;
    }

    // Otherwise, use the event's timezone
    return ev.timezone;
  };

  const fetchTimzone = async (loc) => {
    if (!loc?.lat || !loc?.lng) return;

    try {
      const { data } = await axiosInstance.get(
        `/user/timezone?latitude=${loc.lat}&longitude=${loc.lng}`
      );
      
      if (data.timeZone) {
        console.log("🌍 Timezone set to:", data.timeZone);
        setSelectedTimezone(data.timeZone);
      } else {
        throw new Error("No timezone returned");
      }
    } catch (error) {
      console.warn("Timezone fetch failed:", error.message);
      // Use browser timezone as fallback
      setSelectedTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    }
  };

  //& ==================== EFFECTS ====================
  useEffect(() => {
    fetchConfig();
  }, []);

  //debug userLocation
  useEffect(() => {
    fetchTimzone(userLocation);
    console.log("User Location", userLocation);
  }, [userLocation]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    //* global config
    globalConfig,

    //* location handlers
    userLocation,
    setUserLocation,
    selectedTimezone,
    setSelectedTimezone,
    userLocationLoading,
    setUserLocationLoading,

    //* location handlers
    getEventTimezone,

    //* event handlers
    loading,
    setLoading,

    //* user handlers
    toggleFollowUser,

    preDefinedLocations,
  }), [
    globalConfig,
    userLocation,
    selectedTimezone,
    userLocationLoading,
    loading,
    toggleFollowUser,
  ]);

  if (!globalConfig) return <SuspenseLoader />;

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
};

